import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Modal, { ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import EnrollPictureScreen from '../../components/EnrollPictureScreen/EnrollPictureScreen';
import StepProgress from '../../components/StepProgress/StepProgress';
import { useToast } from '../../components/Toast/ToastContext';
import { SUPPORT_EMAIL } from '../../lib/emails';
import type { AppState } from '../../../lib/store';
import nanoProduct from '../../assets/es-nano-product.jpg';
import nanoDefense from '../../assets/es-nano-defense.jpg';
import sharedStyles from '../../components/EnrollPictureScreen/EnrollPictureScreen.module.scss';
import styles from './ChipsEnrollModal.module.scss';

interface ChipsEnrollModalProps {
  open: boolean;
  onClose: () => void;
}

/** Pragmatic email shape check — backend remains authoritative. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SUPPORT_HREF = `mailto:${SUPPORT_EMAIL}?subject=Fi%20Rescue%20Program%20Enrollment%20Questions`;

/**
 * Picture-cover enrollment prompt for the Fi Nano microchip program.
 *
 *   Step 1 — product hero + program pitch
 *   Step 2 — pairing pitch (Nano as a backup for GPS-tracked dogs)
 *   Step 3 — confirm contact email + submit application
 *
 * Verification is a manual review, so submit fires a mailto to the rescue
 * team with the user's confirmed contact email in the body. No GraphQL
 * mutation exists for self-service enrollment.
 */
export default function ChipsEnrollModal({ open, onClose }: ChipsEnrollModalProps) {
  const session = useSelector((state: AppState) => state.session);
  const { showToast } = useToast();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [email, setEmail] = useState('');
  const emailInputRef = useRef<HTMLInputElement>(null);

  // Snapshot the session email when the modal opens. `session?.email` is
  // intentionally NOT in the dep array: re-running on session changes would
  // overwrite anything the user typed and bounce them back to step 1
  // mid-flow (token refresh, profile edit, etc.).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open) {
      setStep(1);
      setEmail(session?.email ?? '');
    }
  }, [open]);

  function focusEmail() {
    emailInputRef.current?.focus();
    emailInputRef.current?.select();
  }

  function handleSubmit() {
    const subject = encodeURIComponent('Fi Nano Program Enrollment Application');
    const body = encodeURIComponent(
      `Hi rescue team,\n\nI'd like to apply to enroll my rescue in the Fi Nano microchip program.\n\nContact email: ${email}\n\nThanks`,
    );
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    // mailto: returns no signal — a missing handler does nothing silently.
    // The toast tells the user where to look AND gives the fallback address
    // they can type by hand, so a silent-fail isn't a dead end.
    showToast(`Application started in your email client — if it didn't open, email ${SUPPORT_EMAIL}`);
    onClose();
  }

  const submitDisabled = !EMAIL_RE.test(email);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="extra-wide"
      className={sharedStyles.modal}
      backdropClassName={sharedStyles.backdrop}
      showCloseX={false}
    >
      {step === 1 && (
        <EnrollPictureScreen
          heroSrc={nanoProduct}
          heroAlt="Fi Nano microchip product"
          onClose={onClose}
          title="Add microchipping to your rescue program"
          description="Fi Nano chips are free for rescue partners. ISO certified, ICAR registered, and compatible with all scanners. Free to register, free to update, searchable 24/7."
          currentStep={1}
          totalSteps={3}
          highlightCurrentOnly
          actions={
            <Button variant="dark" onClick={() => setStep(2)}>
              Next &rarr;
            </Button>
          }
        />
      )}

      {step === 2 && (
        <EnrollPictureScreen
          heroSrc={nanoDefense}
          heroAlt="Dog with Fi collar outdoors"
          onClose={onClose}
          title="Your last line of defense"
          description="You already track dogs with GPS. Nano is the permanent backup when a collar battery dies or a device is removed. Together, they keep every pet findable."
          currentStep={2}
          totalSteps={3}
          highlightCurrentOnly
          actions={
            <>
              <Button variant="secondary" onClick={() => setStep(1)}>
                &larr; Back
              </Button>
              <Button variant="dark" onClick={() => setStep(3)}>
                Next &rarr;
              </Button>
            </>
          }
        />
      )}

      {step === 3 && (
        <>
          <button
            type="button"
            className={`${sharedStyles.closeX} ${styles.closeXDark}`}
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>

          <form
            className={styles.bodyCentered}
            onSubmit={(e) => {
              e.preventDefault();
              if (!submitDisabled) {
                handleSubmit();
              }
            }}
          >
            <h3>Apply to the Fi Nano Program</h3>
            <p>We&apos;ll send your application to our rescue team for review.</p>

            <div className={styles.emailGroup}>
              <label htmlFor="chips-enroll-email">Email</label>
              <div className={styles.emailRow}>
                <input
                  id="chips-enroll-email"
                  ref={emailInputRef}
                  className={styles.emailInput}
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <button type="button" className={styles.editLink} onClick={focusEmail}>
                  Edit
                </button>
              </div>
            </div>

            <Button type="submit" variant="dark" disabled={submitDisabled} className={styles.submitBtn}>
              Submit application
            </Button>
          </form>

          <div className={sharedStyles.footer}>
            <StepProgress currentStep={3} totalSteps={3} hideLabel highlightCurrentOnly />
            <ModalActions>
              <Button variant="secondary" onClick={() => setStep(2)}>
                &larr; Back
              </Button>
            </ModalActions>
          </div>

          <div className={sharedStyles.support}>
            Have questions? <a href={SUPPORT_HREF}>Contact support</a>
          </div>
        </>
      )}
    </Modal>
  );
}
